/**
 * useRiskDashboard -- Zustand store for the Risk Dashboard.
 */
import { create } from 'zustand'
import { getRiskDashboard, getRiskStressTest, getRiskLimits, getRiskExposure } from '@/lib/api'
import type { RiskDashboard, StressTestResponse, LimitsResponse, ExposureResponse } from '@/lib/types'

interface RiskDashboardState {
  dashboard: RiskDashboard | null
  stressTest: StressTestResponse | null
  limits: LimitsResponse | null
  exposure: ExposureResponse | null
  loading: boolean
  error: string | null
  fetchDashboard: () => Promise<void>
  fetchStressTest: () => Promise<void>
  fetchLimits: () => Promise<void>
  fetchExposure: () => Promise<void>
  fetchAll: () => Promise<void>
}

export const useRiskDashboard = create<RiskDashboardState>()((set) => ({
  dashboard: null,
  stressTest: null,
  limits: null,
  exposure: null,
  loading: false,
  error: null,

  fetchDashboard: async () => {
    set({ loading: true, error: null })
    try {
      const data = await getRiskDashboard()
      set({ dashboard: data, loading: false })
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to fetch risk dashboard'
      set({ loading: false, error: msg })
    }
  },

  fetchStressTest: async () => {
    try {
      const data = await getRiskStressTest()
      set({ stressTest: data })
    } catch { /* silent */ }
  },

  fetchLimits: async () => {
    try {
      const data = await getRiskLimits()
      set({ limits: data })
    } catch { /* silent */ }
  },

  fetchExposure: async () => {
    try {
      const data = await getRiskExposure()
      set({ exposure: data })
    } catch { /* silent */ }
  },

  fetchAll: async () => {
    set({ loading: true, error: null })
    try {
      const [dashboard, stressTest, limits, exposure] = await Promise.all([
        getRiskDashboard(),
        getRiskStressTest(),
        getRiskLimits(),
        getRiskExposure(),
      ])
      set({ dashboard, stressTest, limits, exposure, loading: false })
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to fetch risk data'
      set({ loading: false, error: msg })
    }
  },
}))
